import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Copy, Check, Mail, AlertTriangle, MoreHorizontal, Eye, Edit, Server, CheckCircle2, Clock, XCircle, Settings, Globe, RefreshCw } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { GoogleMapInline, type GoogleMapLocation, type GoogleMapConnection } from '@/components/maps/GoogleMapInline'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { vaultsApi, type VaultItem, type VaultRegion, type ActiveScheduler, type SetupSchedulerResponse } from '../api'

function copyToClipboard(text: string): boolean {
    try {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text)
            return true
        }
    } catch { /* fallback below */ }
    try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        return true
    } catch { return false }
}
import type { ColumnDef } from '@tanstack/react-table'

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
        Active: { bg: 'bg-green-100 dark:bg-green-950/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2 },
        Provisioning: { bg: 'bg-blue-100 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', icon: Clock },
        Pending: { bg: 'bg-amber-100 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', icon: Clock },
        Failed: { bg: 'bg-red-100 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', icon: XCircle },
        Decommissioned: { bg: 'bg-gray-100 dark:bg-gray-950/30', text: 'text-gray-700 dark:text-gray-400', icon: XCircle },
    }
    const c = config[status] || config.Active
    const Icon = c.icon
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
            <Icon className="h-3 w-3" />{status}
        </span>
    )
}

function formatSize(bytes: number): string {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

export function VaultListPage() {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const { formatDateTime } = useDateTimeFormat()

    const [showCreate, setShowCreate] = useState(false)
    const [createForm, setCreateForm] = useState({ description: '', region: '' })
    const [showView, setShowView] = useState<VaultItem | null>(null)
    const [showEdit, setShowEdit] = useState<VaultItem | null>(null)
    const [editForm, setEditForm] = useState({ description: '', analytics: false, bandwidth: 0, latencyPercentage: 0, tcpPercentage: 0, encryptionPercentage: 0 })
    const [showSetup, setShowSetup] = useState<VaultItem | null>(null)
    const [selectedScheduler, setSelectedScheduler] = useState('')
    const [showToken, setShowToken] = useState<SetupSchedulerResponse | null>(null)
    const [copied, setCopied] = useState(false)
    const [tokenSaved, setTokenSaved] = useState(false)
    const [showTokenWarning, setShowTokenWarning] = useState(false)

    // Fetch vaults
    const { data: vaultsData, isLoading } = useQuery({
        queryKey: ['vaults'],
        queryFn: async () => {
            const res = await vaultsApi.list()
            const d = res.data as any
            return (d?.data || []) as VaultItem[]
        },
        enabled: isAuthenticated,
    })

    // Fetch regions for create dialog
    const { data: regionsData } = useQuery({
        queryKey: ['vault-regions'],
        queryFn: async () => {
            const res = await vaultsApi.getRegions()
            const d = res.data as any
            return (d?.data || []) as VaultRegion[]
        },
        enabled: isAuthenticated && showCreate,
    })

    // Fetch vault map data
    const { data: mapData } = useQuery({
        queryKey: ['vault-map-data'],
        queryFn: async () => {
            const res = await (await import('@/lib/api/axios')).default.get('/api/vaults/map/data')
            const d = res.data as any
            return (d?.data || []) as GoogleMapLocation[]
        },
        enabled: isAuthenticated,
    })

    // Fetch paths data (locations + vaults + connections)
    const { data: pathsData } = useQuery({
        queryKey: ['vault-paths'],
        queryFn: async () => {
            const res = await (await import('@/lib/api/axios')).default.get('/api/vaults/paths')
            const d = (res.data as any)?.data
            return d as { locations: GoogleMapLocation[]; connections: GoogleMapConnection[] }
        },
        enabled: isAuthenticated,
    })

    // Fetch active schedulers for setup dialog
    const { data: schedulersData } = useQuery({
        queryKey: ['active-schedulers'],
        queryFn: async () => {
            const res = await vaultsApi.getActiveSchedulers()
            const d = res.data as any
            return (d?.data || []) as ActiveScheduler[]
        },
        enabled: isAuthenticated && !!showSetup,
    })

    const vaults = vaultsData || []
    const regions = regionsData || []
    const schedulers = schedulersData || []

    // Create vault
    const createMut = useMutation({
        mutationFn: () => vaultsApi.create({
            description: createForm.description,
            region: createForm.region,
        }),
        onSuccess: () => {
            setShowCreate(false)
            setCreateForm({ description: '', region: '' })
            queryClient.invalidateQueries({ queryKey: ['vaults'] })
            toast({ title: 'Vault creation requested', description: 'The vault will be provisioned by CloudFormation.' })
        },
        onError: () => toast({ title: 'Failed to create vault', variant: 'destructive' }),
    })

    // Update vault
    const updateMut = useMutation({
        mutationFn: ({ id, data }: { id: number; data: Parameters<typeof vaultsApi.update>[1] }) =>
            vaultsApi.update(id, data),
        onSuccess: () => {
            setShowEdit(null)
            queryClient.invalidateQueries({ queryKey: ['vaults'] })
            toast({ title: 'Vault updated' })
        },
        onError: () => toast({ title: 'Failed to update vault', variant: 'destructive' }),
    })

    // Setup scheduler token
    const setupMut = useMutation({
        mutationFn: ({ vaultId, schedulerId }: { vaultId: number; schedulerId: string }) =>
            vaultsApi.setupScheduler(vaultId, schedulerId),
        onSuccess: (res) => {
            const d = (res.data as any)?.data || res.data
            setShowSetup(null)
            setSelectedScheduler('')
            setShowToken(d as SetupSchedulerResponse)
            setCopied(false)
            setTokenSaved(false)
            setShowTokenWarning(false)
        },
        onError: () => toast({ title: 'Failed to generate token', variant: 'destructive' }),
    })

    const columns: ColumnDef<VaultItem>[] = [
        {
            accessorKey: 'vaultId',
            header: 'Vault ID',
            cell: ({ row }) => <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.original.vaultId}</code>,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: ({ row }) => (
                <div className="text-sm font-medium">{row.original.description ? <TranslatableText text={row.original.description} /> : '-'}</div>
            ),
        },
        {
            accessorKey: 'regionName',
            header: 'Region',
            size: 220,
            cell: ({ row }) => (
                <div className="flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm">{row.original.regionName ? <TranslatableText text={row.original.regionName} /> : '-'}</span>
                </div>
            ),
        },
        {
            accessorKey: 'operationalStatus',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.operationalStatus} />,
        },
        {
            accessorKey: 'objectCount',
            header: 'Objects',
            size: 80,
            cell: ({ row }) => <span className="text-sm">{(row.original.objectCount || 0).toLocaleString()}</span>,
        },
        {
            accessorKey: 'versionCount',
            header: 'Versions',
            size: 80,
            cell: ({ row }) => <span className="text-sm">{(row.original.versionCount || 0).toLocaleString()}</span>,
        },
        {
            accessorKey: 'size',
            header: 'Size',
            size: 80,
            cell: ({ row }) => <span className="text-sm">{formatSize(row.original.size)}</span>,
        },
        {
            accessorKey: 'created',
            header: 'Created',
            cell: ({ row }) => row.original.created
                ? <span className="text-sm">{formatDateTime(row.original.created)}</span>
                : <span className="text-sm text-muted-foreground">-</span>,
        },
        {
            id: 'actions',
            header: '',
            size: 50,
            cell: ({ row }) => {
                const v = row.original
                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => setShowView(v)}>
                                    <Eye className="mr-2 h-4 w-4" />View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                    setShowEdit(v)
                                    setEditForm({
                                        description: v.description,
                                        analytics: v.analytics,
                                        bandwidth: v.bandwidth,
                                        latencyPercentage: v.latencyPercentage,
                                        tcpPercentage: v.tcpPercentage,
                                        encryptionPercentage: v.encryptionPercentage,
                                    })
                                }}>
                                    <Edit className="mr-2 h-4 w-4" />Edit Vault
                                </DropdownMenuItem>
                                {v.operationalStatus === 'Active' && (
                                    <DropdownMenuItem onClick={() => { setShowSetup(v); setSelectedScheduler('') }}>
                                        <Settings className="mr-2 h-4 w-4" />Setup Scheduler
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Vaults</h1>
                    <p className="text-muted-foreground">Manage vaults</p>
                </div>
                <Button onClick={() => setShowCreate(true)}>
                    <Plus className="h-4 w-4 mr-2" />Create Vault
                </Button>
            </div>

            {/* Map */}
            <GoogleMapInline
                locations={mapData || []}
                pathLocations={pathsData?.locations}
                pathConnections={pathsData?.connections}
                compact
            />

            {isLoading ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
            ) : vaults.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 border rounded-lg bg-muted/30">
                    <Server className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">No vaults configured</p>
                    <p className="text-xs text-muted-foreground mt-1">Create a vault to start storing backups</p>
                </div>
            ) : (
                <DataTable columns={columns} data={vaults} searchKey="description" searchPlaceholder="Search vaults..." />
            )}

            {/* Create Vault Dialog */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Create Vault</h3>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">Description</Label>
                                <Input
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="e.g. UK Production Vault, US DR Vault"
                                    className="text-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-sm">Region</Label>
                                <select
                                    value={createForm.region}
                                    onChange={(e) => setCreateForm(f => ({ ...f, region: e.target.value }))}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select region...</option>
                                    {regions.map(r => (
                                        <option key={r.code} value={r.code}><TranslatableText text={r.name} /></option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 mt-4">
                            <Clock className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-blue-700 dark:text-blue-400">
                                Vault creation is handled by CloudFormation. The vault will appear as &quot;Provisioning&quot; until the infrastructure is deployed (typically 5-10 minutes).
                            </span>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button size="sm" onClick={() => createMut.mutate()} disabled={!createForm.description || !createForm.region || createMut.isPending}>
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                Create Vault
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Vault Dialog */}
            {showView && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Vault Details</h3>
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                                <span className="text-muted-foreground">Vault ID</span>
                                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{showView.vaultId}</code>
                                <span className="text-muted-foreground">Description</span>
                                <span className="font-medium">{showView.description ? <TranslatableText text={showView.description} /> : '-'}</span>
                                <span className="text-muted-foreground">Region</span>
                                <span className="font-medium">{showView.regionName || '-'}</span>
                                <span className="text-muted-foreground">Status</span>
                                <span><StatusBadge status={showView.operationalStatus} /></span>
                                <span className="text-muted-foreground">Objects</span>
                                <span>{(showView.objectCount || 0).toLocaleString()}</span>
                                <span className="text-muted-foreground">Versions</span>
                                <span>{(showView.versionCount || 0).toLocaleString()}</span>
                                <span className="text-muted-foreground">Size</span>
                                <span>{formatSize(showView.size)}</span>
                                <span className="text-muted-foreground">Analytics</span>
                                <span>{showView.analytics ? 'Enabled' : 'Disabled'}</span>
                                <span className="text-muted-foreground">Bandwidth</span>
                                <span>{showView.bandwidth} Mbps</span>
                                <span className="text-muted-foreground">Latency</span>
                                <span>{showView.latencyPercentage}%</span>
                                <span className="text-muted-foreground">TCP Overhead</span>
                                <span>{showView.tcpPercentage}%</span>
                                <span className="text-muted-foreground">Encryption</span>
                                <span>{showView.encryptionPercentage}%</span>
                                <span className="text-muted-foreground">Created</span>
                                <span>{showView.created ? formatDateTime(showView.created) : '-'}</span>
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => setShowView(null)}>Close</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Vault Dialog */}
            {showEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Edit Vault</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            {showEdit.vaultId} — {showEdit.regionName}
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">Description</Label>
                                <Input
                                    value={editForm.description}
                                    onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                                    className="text-sm mt-1"
                                />
                            </div>
                            <div className="flex items-center justify-between rounded-lg border p-4">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">Analytics</Label>
                                    <p className="text-xs text-muted-foreground">Run analytics scanning on objects in this vault</p>
                                </div>
                                <Switch
                                    checked={editForm.analytics}
                                    onCheckedChange={(checked) => setEditForm(f => ({ ...f, analytics: checked }))}
                                />
                            </div>
                            <div className="space-y-3">
                                <Label className="text-sm font-medium">Network Information</Label>
                                <p className="text-xs text-muted-foreground -mt-2">Used to calculate accurate recovery time estimates</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Bandwidth (Mbps)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={editForm.bandwidth}
                                            onChange={(e) => setEditForm(f => ({ ...f, bandwidth: parseInt(e.target.value) || 0 }))}
                                            className="text-sm mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Latency (%)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={editForm.latencyPercentage}
                                            onChange={(e) => setEditForm(f => ({ ...f, latencyPercentage: parseInt(e.target.value) || 0 }))}
                                            className="text-sm mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">TCP Overhead (%)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={editForm.tcpPercentage}
                                            onChange={(e) => setEditForm(f => ({ ...f, tcpPercentage: parseInt(e.target.value) || 0 }))}
                                            className="text-sm mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Encryption Overhead (%)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={editForm.encryptionPercentage}
                                            onChange={(e) => setEditForm(f => ({ ...f, encryptionPercentage: parseInt(e.target.value) || 0 }))}
                                            className="text-sm mt-1"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowEdit(null)}>Cancel</Button>
                            <Button size="sm" disabled={updateMut.isPending} onClick={() => updateMut.mutate({ id: showEdit.id, data: editForm })}>
                                {updateMut.isPending ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Setup Scheduler Dialog */}
            {showSetup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Setup Scheduler</h3>
                        <p className="text-sm text-muted-foreground mb-1">
                            Vault: <span className="font-medium text-foreground"><TranslatableText text={showSetup.description} /></span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            Generate a connection token for a scheduler to access this vault.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm">Scheduler</Label>
                                <select
                                    value={selectedScheduler}
                                    onChange={(e) => setSelectedScheduler(e.target.value)}
                                    className="w-full mt-1 rounded-md border bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select scheduler...</option>
                                    {schedulers.map(s => (
                                        <option key={s.schedulerId} value={s.schedulerId}><TranslatableText text={s.description} /></option>
                                    ))}
                                </select>
                                {schedulers.length === 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">No active schedulers available. Register a scheduler first.</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" size="sm" onClick={() => setShowSetup(null)}>Cancel</Button>
                            <Button
                                size="sm"
                                disabled={!selectedScheduler || setupMut.isPending}
                                onClick={() => setupMut.mutate({ vaultId: showSetup.id, schedulerId: selectedScheduler })}
                            >
                                <Settings className="h-3.5 w-3.5 mr-1.5" />
                                Generate Token
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Vault Connection Token Display */}
            {showToken && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-lg w-full mx-4">
                        <h3 className="text-lg font-semibold mb-2">Vault Connection Token</h3>
                        <p className="text-sm text-muted-foreground mb-1">
                            Vault: <span className="font-medium text-foreground">{showToken.vaultDescription}</span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            Scheduler: <span className="font-medium text-foreground">{showToken.schedulerDescription}</span>
                        </p>
                        <div>
                            <Label className="text-xs text-muted-foreground">Connection Token</Label>
                            <textarea
                                readOnly
                                value={showToken.vaultConnectionToken}
                                className="w-full h-24 mt-1 rounded-md border bg-muted px-3 py-2 font-mono text-[10px] resize-none select-all"
                            />
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={() => {
                                copyToClipboard(showToken.vaultConnectionToken)
                                setCopied(true)
                                setTokenSaved(true)
                            }}>
                                {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                {copied ? 'Copied' : 'Copy to Clipboard'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => {
                                const subject = encodeURIComponent(`VaultSentinel Vault Connection Token — ${showToken.vaultDescription}`)
                                const body = encodeURIComponent(
                                    `Please add this vault connection to the scheduler (Settings → Vault Connections).\n\nVault: ${showToken.vaultDescription}\nScheduler: ${showToken.schedulerDescription}\n\nToken:\n${showToken.vaultConnectionToken}\n\nThis token contains the vault configuration needed for the scheduler to upload backups.`
                                )
                                window.open(`mailto:?subject=${subject}&body=${body}`)
                                setTokenSaved(true)
                            }}>
                                <Mail className="h-3.5 w-3.5 mr-1.5" />Email to Engineer
                            </Button>
                        </div>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 mt-4">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-yellow-700 dark:text-yellow-400">
                                This token contains the vault connection details for the scheduler. Copy or email it now — it will not be shown again.
                            </span>
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button size="sm" onClick={() => {
                                if (!tokenSaved) {
                                    setShowTokenWarning(true)
                                } else {
                                    setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                }
                            }}>Done</Button>
                        </div>
                        {showTokenWarning && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 mt-3">
                                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Token not saved</p>
                                    <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                                        You haven't copied the token or emailed it. This token will not be available after closing this window. Are you sure?
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowTokenWarning(false)}>
                                            Go Back
                                        </Button>
                                        <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => {
                                            setShowToken(null); setCopied(false); setTokenSaved(false); setShowTokenWarning(false)
                                        }}>
                                            Close Anyway
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
